import { Checkbox, Stack } from '@mantine/core'
import { DateInput } from '@mantine/dates'
import { useForm, zodResolver } from '@mantine/form'
import { ContextModalProps } from '@mantine/modals'
import Form from 'common/Form'
import NumberWithUnitInput from 'common/inputs/NumberWithUnitInput'
import { useUpdateShortageOrder } from 'hooks/shortage'
import { useState } from 'react'
import { fulfillShortageOrderSchema } from 'types/schemas'
import { ShortageOrderTableItem } from 'types/shortage'
import { trimFormValues } from 'utils'
import z from 'zod'

type Props = {
  shortageOrder: ShortageOrderTableItem
}

type FulfillShortageOrder = z.infer<typeof fulfillShortageOrderSchema>

export default function FulfillShortageOrderModal({
  context,
  id,
  innerProps: { shortageOrder },
}: ContextModalProps<Props>) {
  const updateShortageOrder = useUpdateShortageOrder()
  const [isUnfulfilled, setIsUnfulfilled] = useState(false)

  async function onSubmit(formValues: FulfillShortageOrder) {
    await updateShortageOrder.mutateAsync({
      id: shortageOrder.id,
      fulfilledAt: formValues.fulfilledAt,
      fulfilledAmount: isUnfulfilled ? -1 : formValues.amount,
    })
  }

  const form = useForm<FulfillShortageOrder>({
    validate: (values) => {
      if (isUnfulfilled) {
        const { amount, ...rest } = zodResolver(fulfillShortageOrderSchema)(values)
        return rest
      }
      return zodResolver(fulfillShortageOrderSchema)(values)
    },
    initialValues: {
      fulfilledAt: new Date(),
      amount: shortageOrder.orderedAmount,
      unit: shortageOrder.unit,
    },
    transformValues: trimFormValues,
  })

  return (
    <Form onSubmit={onSubmit} form={form} onCancel={() => context.closeModal(id)} submitText="Zrealizuj zamówienie">
      <Stack gap="xs">
        <DateInput
          label="Data realizacji"
          description="Data, kiedy zamówienie zostało zrealizowane"
          locale="pl"
          valueFormat="DD.MM.YYYY"
          {...form.getInputProps('fulfilledAt')}
        />
        <NumberWithUnitInput
          label="Zrealizowana ilość"
          description="Ilość produktu jaką zrealizowano."
          availableUnits={[shortageOrder.unit]}
          form={form}
          disabled={isUnfulfilled}
          placeholder={isUnfulfilled ? '' : undefined}
          withAsterisk
        />
        <Checkbox
          label="Zamówienie niezrealizowane"
          description="Zaznacz, jeśli zamówienie nie zostało zrealizowane. Braki zostaną oznaczone jako niezrealizowane i trzeba będzie je ponownie zamówić."
          checked={isUnfulfilled}
          onChange={(event) => {
            setIsUnfulfilled(event.currentTarget.checked)
            if (event.currentTarget.checked) {
              form.setFieldValue('amount', undefined!)
            } else {
              form.setFieldValue('amount', shortageOrder.orderedAmount)
            }
          }}
        />
      </Stack>
    </Form>
  )
}
